import type { LevelDefinition } from "@/domain";

export interface PictureMatchItem {
  id: string;
  asset: string;
  alt: string;
}

export interface CharacterMatchItem {
  id: string;
  label: string;
}

export interface PictureCharacterAnswer {
  pictureId: string;
  characterId: string;
}

export interface PictureCharacterMatchPayload {
  pictureItems: PictureMatchItem[];
  characterItems: CharacterMatchItem[];
  matches: PictureCharacterAnswer[];
  hint: string;
}

export const pictureCharacterMatchLevels: LevelDefinition<PictureCharacterMatchPayload>[] = [
  {
    id: "chinese-picture-character-match-1",
    domain: "chinese",
    activityType: "picture-character-match",
    difficulty: 1,
    objectives: ["认识“山”和“水”", "把具体图画和汉字意思连起来"],
    title: "山和水",
    instruction: "图和字在找朋友。先点图，再找意思一样的汉字。",
    promptAudio: "/audio/chinese/picture-character-match/instruction.ogg",
    assets: [
      "/assets/chinese/concepts/mountain.webp",
      "/assets/chinese/concepts/water-drop.webp"
    ],
    payload: {
      pictureItems: [
        {
          id: "mountain-picture",
          asset: "/assets/chinese/concepts/mountain.webp",
          alt: "一座小山"
        },
        {
          id: "water-picture",
          asset: "/assets/chinese/concepts/water-drop.webp",
          alt: "一滴清水"
        }
      ],
      characterItems: [
        { id: "water-character", label: "水" },
        { id: "mountain-character", label: "山" }
      ],
      matches: [
        {
          pictureId: "mountain-picture",
          characterId: "mountain-character"
        },
        {
          pictureId: "water-picture",
          characterId: "water-character"
        }
      ],
      hint: "轻声说出图里的东西，再找一找它的汉字朋友。"
    }
  },
  {
    id: "chinese-picture-character-match-2",
    domain: "chinese",
    activityType: "picture-character-match",
    difficulty: 2,
    objectives: ["认识“火”“日”“月”", "分辨三个常见自然事物汉字"],
    title: "天空和火光",
    instruction: "图和字在找朋友。先点图，再找意思一样的汉字。",
    promptAudio: "/audio/chinese/picture-character-match/instruction.ogg",
    assets: [
      "/assets/chinese/concepts/flame.webp",
      "/assets/chinese/concepts/sun.webp",
      "/assets/chinese/concepts/moon.webp"
    ],
    payload: {
      pictureItems: [
        {
          id: "fire-picture",
          asset: "/assets/chinese/concepts/flame.webp",
          alt: "一团火焰"
        },
        {
          id: "sun-picture",
          asset: "/assets/chinese/concepts/sun.webp",
          alt: "圆圆的太阳"
        },
        {
          id: "moon-picture",
          asset: "/assets/chinese/concepts/moon.webp",
          alt: "弯弯的月亮"
        }
      ],
      characterItems: [
        { id: "moon-character", label: "月" },
        { id: "fire-character", label: "火" },
        { id: "sun-character", label: "日" }
      ],
      matches: [
        { pictureId: "fire-picture", characterId: "fire-character" },
        { pictureId: "sun-picture", characterId: "sun-character" },
        { pictureId: "moon-picture", characterId: "moon-character" }
      ],
      hint: "太阳和月亮最熟悉啦，先帮它们找到汉字朋友。"
    }
  },
  {
    id: "chinese-picture-character-match-3",
    domain: "chinese",
    activityType: "picture-character-match",
    difficulty: 3,
    objectives: ["认识“木”“人”“口”", "观察汉字和事物外形的联系"],
    title: "身边的字",
    instruction: "图和字在找朋友。先点图，再找意思一样的汉字。",
    promptAudio: "/audio/chinese/picture-character-match/instruction.ogg",
    assets: [
      "/assets/chinese/concepts/tree.webp",
      "/assets/chinese/concepts/person.webp",
      "/assets/chinese/concepts/mouth.webp"
    ],
    payload: {
      pictureItems: [
        {
          id: "tree-picture",
          asset: "/assets/chinese/concepts/tree.webp",
          alt: "一棵大树"
        },
        {
          id: "person-picture",
          asset: "/assets/chinese/concepts/person.webp",
          alt: "站着的小朋友"
        },
        {
          id: "mouth-picture",
          asset: "/assets/chinese/concepts/mouth.webp",
          alt: "一张嘴巴"
        }
      ],
      characterItems: [
        { id: "mouth-character", label: "口" },
        { id: "tree-character", label: "木" },
        { id: "person-character", label: "人" }
      ],
      matches: [
        { pictureId: "tree-picture", characterId: "tree-character" },
        { pictureId: "person-picture", characterId: "person-character" },
        { pictureId: "mouth-picture", characterId: "mouth-character" }
      ],
      hint: "把图名轻轻念出来，汉字朋友会更好找。"
    }
  },
  {
    id: "chinese-picture-character-match-4",
    domain: "chinese",
    activityType: "picture-character-match",
    difficulty: 4,
    objectives: ["认识“手”“足”“门”", "理解常见身体和物品汉字"],
    title: "手足和门",
    instruction: "图和字在找朋友。先点图，再找意思一样的汉字。",
    promptAudio: "/audio/chinese/picture-character-match/instruction.ogg",
    assets: [
      "/assets/chinese/concepts/hand.webp",
      "/assets/chinese/concepts/foot.webp",
      "/assets/chinese/concepts/door.webp"
    ],
    payload: {
      pictureItems: [
        {
          id: "hand-picture",
          asset: "/assets/chinese/concepts/hand.webp",
          alt: "张开的手"
        },
        {
          id: "foot-picture",
          asset: "/assets/chinese/concepts/foot.webp",
          alt: "一只脚"
        },
        {
          id: "door-picture",
          asset: "/assets/chinese/concepts/door.webp",
          alt: "打开的门"
        }
      ],
      characterItems: [
        { id: "door-character", label: "门" },
        { id: "hand-character", label: "手" },
        { id: "foot-character", label: "足" }
      ],
      matches: [
        { pictureId: "hand-picture", characterId: "hand-character" },
        { pictureId: "foot-picture", characterId: "foot-character" },
        { pictureId: "door-picture", characterId: "door-character" }
      ],
      hint: "看看自己的手和脚，它们会先来帮忙。"
    }
  },
  {
    id: "chinese-picture-character-match-5",
    domain: "chinese",
    activityType: "picture-character-match",
    difficulty: 5,
    objectives: ["认识“田”“云”“雨”“花”", "分辨四个自然景物汉字"],
    title: "田野天气",
    instruction: "图和字在找朋友。先点图，再找意思一样的汉字。",
    promptAudio: "/audio/chinese/picture-character-match/instruction.ogg",
    assets: [
      "/assets/chinese/concepts/field.webp",
      "/assets/chinese/concepts/cloud.webp",
      "/assets/chinese/concepts/rain.webp",
      "/assets/chinese/concepts/flower.webp"
    ],
    payload: {
      pictureItems: [
        {
          id: "field-picture",
          asset: "/assets/chinese/concepts/field.webp",
          alt: "整齐的田地"
        },
        {
          id: "cloud-picture",
          asset: "/assets/chinese/concepts/cloud.webp",
          alt: "一朵白云"
        },
        {
          id: "rain-picture",
          asset: "/assets/chinese/concepts/rain.webp",
          alt: "落下的雨滴"
        },
        {
          id: "flower-picture",
          asset: "/assets/chinese/concepts/flower.webp",
          alt: "一朵小花"
        }
      ],
      characterItems: [
        { id: "rain-character", label: "雨" },
        { id: "flower-character", label: "花" },
        { id: "field-character", label: "田" },
        { id: "cloud-character", label: "云" }
      ],
      matches: [
        { pictureId: "field-picture", characterId: "field-character" },
        { pictureId: "cloud-picture", characterId: "cloud-character" },
        { pictureId: "rain-picture", characterId: "rain-character" },
        { pictureId: "flower-picture", characterId: "flower-character" }
      ],
      hint: "一幅一幅念出名字，慢慢把汉字朋友找齐。"
    }
  },
  {
    id: "chinese-picture-character-match-6",
    domain: "chinese",
    activityType: "picture-character-match",
    difficulty: 6,
    objectives: ["认识“鱼”“鸟”“车”“书”", "在四组常见事物中稳定配对"],
    title: "熟悉的事物",
    instruction: "图和字在找朋友。先点图，再找意思一样的汉字。",
    promptAudio: "/audio/chinese/picture-character-match/instruction.ogg",
    assets: [
      "/assets/chinese/concepts/fish.webp",
      "/assets/chinese/concepts/bird.webp",
      "/assets/chinese/concepts/car.webp",
      "/assets/chinese/concepts/book.webp"
    ],
    payload: {
      pictureItems: [
        {
          id: "fish-picture",
          asset: "/assets/chinese/concepts/fish.webp",
          alt: "游动的小鱼"
        },
        {
          id: "bird-picture",
          asset: "/assets/chinese/concepts/bird.webp",
          alt: "飞翔的小鸟"
        },
        {
          id: "car-picture",
          asset: "/assets/chinese/concepts/car.webp",
          alt: "一辆小车"
        },
        {
          id: "book-picture",
          asset: "/assets/chinese/concepts/book.webp",
          alt: "合上的书本"
        }
      ],
      characterItems: [
        { id: "book-character", label: "书" },
        { id: "fish-character", label: "鱼" },
        { id: "car-character", label: "车" },
        { id: "bird-character", label: "鸟" }
      ],
      matches: [
        { pictureId: "fish-picture", characterId: "fish-character" },
        { pictureId: "bird-picture", characterId: "bird-character" },
        { pictureId: "car-picture", characterId: "car-character" },
        { pictureId: "book-picture", characterId: "book-character" }
      ],
      hint: "先牵起最熟悉的一对，剩下的朋友就更好找啦。"
    }
  }
];
